import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Sprout,
  Users,
  CalendarDays,
  HandHeart,
  ShieldCheck,
  ArrowRight,
  Phone,
  Mail,
} from "lucide-react";
import {
  ButtonLink,
  Section,
  Card,
  Eyebrow,
  PlaceholderImage,
} from "../components/ui-kit";
import { MOSAIC } from "../data/site";
import { useContact } from "../context/SiteSettingsContext";
import { StayConnected } from "../components/StayConnected";
import { WaveDivider } from "../components/WaveDivider";
import { Lightbox } from "../components/Lightbox";
import { StatsStrip } from "../components/StatsStrip";
import { PhotoRibbon } from "../components/PhotoRibbon";
import { Testimonials } from "../components/Testimonials";
import { HeroVideo } from "../components/HeroVideo";
import { FacebookFeed } from "../components/FacebookFeed";

const HERO =
  "https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/m43l3sbf_IMG-20260520-WA0028.jpg";

const DIFFERENCE = [
  {
    icon: Heart,
    tone: "orange",
    testid: "difference-card-person",
    title: "Person-centred days",
    text: "Every day is shaped around the person, their interests, their goals and the pace that suits them.",
  },
  {
    icon: Sprout,
    tone: "teal",
    testid: "difference-card-real",
    title: "Real independence",
    text: "We build practical life skills that carry over into everyday living, at our own pace and without pressure.",
  },
  {
    icon: Users,
    tone: "sky",
    testid: "difference-card-genuine",
    title: "Genuine belonging",
    text: "People we support build friendships and feel part of a warm, welcoming community.",
  },
];

const TONES = {
  orange: "bg-orange-100 text-orange-700",
  teal: "bg-teal-100 text-teal-700",
  sky: "bg-sky-100 text-sky-700",
};

const FeatureCard = ({ icon: Icon, tone, title, text, testid }) => (
  <Card data-testid={testid} className="motion-safe:transition-transform motion-safe:duration-300 motion-safe:hover:-translate-y-1">
    <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${TONES[tone]}`}>
      <Icon className="h-7 w-7" aria-hidden="true" />
    </span>
    <h3 className="mt-5 font-heading text-xl font-semibold text-stone-900">{title}</h3>
    <p className="mt-3 text-lg text-stone-700 leading-relaxed">{text}</p>
  </Card>
);

export default function Home() {
  const CONTACT = useContact();
  const [lightbox, setLightbox] = useState(null);
  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-stone-900" data-testid="home-hero">
        <img
          src={HERO}
          alt="A happy group of people we support smiling together on a day out."
          className="absolute inset-0 h-full w-full object-cover object-center ken-burns"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-stone-950/90 via-stone-900/75 to-stone-900/40"
          aria-hidden="true"
        />
        <div className="grain pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 lg:py-32">
          <div className="flex flex-col gap-12 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-6 motion-safe:duration-1000">
            <p className="text-base font-semibold uppercase tracking-wide text-orange-300 mb-3">
              Weekday support, Monday to Friday
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Supporting young adults to build skills, confidence and connection.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-stone-200 leading-relaxed max-w-xl">
              Spire Inclusion provides active, person-centred weekday support for young adults
              with disabilities. Every day is shaped around meaningful activities, independence,
              wellbeing and belonging.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <ButtonLink to="/contact" data-testid="hero-enquiry-button">
                Make an enquiry
              </ButtonLink>
              <ButtonLink
                to="/what-we-do"
                variant="secondary"
                className="!bg-white/10 !text-white !border-white/40 hover:!bg-white/20"
                data-testid="hero-discover-button"
              >
                Discover what we do
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </ButtonLink>
            </div>
          </div>
          <HeroVideo />
          </div>
        </div>
      </section>
      <div className="bg-stone-900" aria-hidden="true">
        <WaveDivider fill="#fafaf9" />
      </div>

      {/* What makes us different */}
      <Section tone="default" data-testid="home-difference">
        <div className="max-w-3xl">
          <Eyebrow>Why Spire Inclusion</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
            What makes Spire Inclusion different
          </h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            We focus on the things that make a real difference to a person's day: feeling capable,
            feeling connected and feeling valued. Support is built around each person, never the
            other way around.
          </p>
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {DIFFERENCE.map((d) => (
            <FeatureCard key={d.title} {...d} />
          ))}
        </div>
      </Section>

      <StatsStrip />
      <PhotoRibbon />

      {/* Latest from Facebook */}
      <Section tone="white" data-testid="home-facebook">
        <div className="max-w-3xl mb-10">
          <Eyebrow>Straight from our page</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
            Latest from Facebook
          </h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            See what we've been up to lately, direct from our Facebook page.
          </p>
        </div>
        <FacebookFeed
          title="Follow us on Facebook"
          subtitle="Fresh photos and updates from our page."
          height={560}
        />
      </Section>

      {/* Life skills and independence */}
      <Section tone="white" data-testid="home-life-skills">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <PlaceholderImage
            src="https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/kq5fn77i_IMG-20260619-WA0004.jpg"
            alt="A support worker helping someone we support measure baking ingredients during a cooking session."
          />
          <div>
            <Eyebrow>Life skills</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
              Life skills and independence
            </h2>
            <p className="mt-4 text-lg text-stone-700 leading-relaxed">
              From cooking and budgeting to travel and everyday routines, we help people we support
              build practical skills at a pace that feels right. Small, steady steps lead to lasting
              confidence and greater independence.
            </p>
            <Link
              to="/how-we-support-people"
              className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-orange-700 hover:text-orange-800"
              data-testid="life-skills-link"
            >
              How we support people
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Activities and community */}
      <Section tone="default" data-testid="home-activities">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="lg:order-2">
            <Eyebrow>Out and about</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
              Activities and community involvement
            </h2>
            <p className="mt-4 text-lg text-stone-700 leading-relaxed">
              Days are active and meaningful. We take part in our local community, enjoy creative
              and physical activities, and explore new interests together. Being part of the wider
              community is at the heart of what we do.
            </p>
            <Link
              to="/what-we-do"
              className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-orange-700 hover:text-orange-800"
              data-testid="activities-link"
            >
              Explore what we do
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
          <PlaceholderImage
            src="https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/hjrnzf5p_IMG-20260619-WA0003.jpg"
            alt="A group from Spire Inclusion enjoying a trip to the cinema together in the community."
            className="lg:order-1"
          />
        </div>
      </Section>

      {/* A typical week */}
      <Section tone="tinted" data-testid="home-typical-week">
        <div className="max-w-3xl">
          <Eyebrow>What a week looks like</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
            A typical week
          </h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            No two people are the same, so no two weeks look the same. A balanced week might blend
            life skills, community trips, creative sessions, wellbeing time and social connection.
          </p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[
            { day: "Monday", text: "Cooking, then arts and crafts." },
            { day: "Tuesday", text: "Accessing the community, then life skills & cooking." },
            { day: "Wednesday", text: "Swimming, then a trip to the pub." },
            { day: "Thursday", text: "Sporting activities, then performing arts." },
            { day: "Friday", text: "Hockey and activities, then a chilled afternoon." },
          ].map(({ day, text }) => (
            <div
              key={day}
              className="rounded-2xl bg-white border border-stone-200 p-5"
              data-testid={`week-day-${day.toLowerCase()}`}
            >
              <p className="font-heading font-semibold text-stone-900">{day}</p>
              <p className="mt-2 text-base text-stone-600">{text}</p>
            </div>
          ))}
        </div>
        <ButtonLink to="/a-typical-week" variant="secondary" className="mt-10" data-testid="typical-week-button">
          See a sample week
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </ButtonLink>
      </Section>

      {/* Families and carers */}
      <Section tone="white" data-testid="home-families">
        <div className="grid gap-8 lg:grid-cols-2">
          <Card>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
              <HandHeart className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-5 font-heading text-2xl font-semibold text-stone-900">
              Families and carers
            </h2>
            <p className="mt-3 text-lg text-stone-700 leading-relaxed">
              We know families and carers want reassurance and clear communication. We work in
              partnership with you, keep you informed, and welcome your knowledge of the person we
              support.
            </p>
            <Link
              to="/families-and-carers"
              className="mt-5 inline-flex items-center gap-2 text-lg font-semibold text-orange-700 hover:text-orange-800"
              data-testid="families-link"
            >
              Support for families and carers
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </Card>
          <Card>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
              <ShieldCheck className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-5 font-heading text-2xl font-semibold text-stone-900">
              Safeguarding and wellbeing
            </h2>
            <p className="mt-3 text-lg text-stone-700 leading-relaxed">
              Everyone has the right to feel safe, respected and well. Safeguarding and wellbeing
              guide everything we do, and we take any concern seriously.
            </p>
            <Link
              to="/safeguarding-and-complaints"
              className="mt-5 inline-flex items-center gap-2 text-lg font-semibold text-orange-700 hover:text-orange-800"
              data-testid="safeguarding-link"
            >
              Safeguarding and complaints
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </Card>
        </div>
      </Section>

      {/* Photo mosaic */}
      <Section tone="white" data-testid="home-mosaic">
        <div className="max-w-3xl">
          <Eyebrow>Life at Spire Inclusion</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
            Moments from our week
          </h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            A glimpse of the real activities, days out and friendships that make up life at Spire
            Inclusion.
          </p>
        </div>
        <div className="mt-10 columns-2 md:columns-3 gap-3 sm:gap-4">
          {MOSAIC.map((m, i) => (
            <button
              key={m.src}
              type="button"
              onClick={() => setLightbox(i)}
              aria-label={`View larger photo: ${m.alt}`}
              className="group mb-3 sm:mb-4 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-stone-200 bg-stone-200"
              data-testid={`mosaic-photo-${i}`}
            >
              <img
                src={m.src}
                alt={m.alt}
                loading="lazy"
                className="w-full h-auto object-cover motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-105"
              />
            </button>
          ))}
        </div>
        <ButtonLink to="/what-we-do" variant="secondary" className="mt-10" data-testid="mosaic-button">
          See more of what we do
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </ButtonLink>
      </Section>

      {/* Testimonials */}
      <Testimonials />

      {/* Stay connected */}
      <StayConnected tone="white" />

      {/* Contact CTA */}
      <Section tone="stone" data-testid="home-contact-cta">
        <div className="rounded-3xl bg-stone-900 p-8 sm:p-12 lg:p-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                Let's talk about what's possible
              </h2>
              <p className="mt-4 text-lg text-stone-300 leading-relaxed">
                Whether you are exploring support for yourself, a family member, or making a
                referral, we are happy to answer your questions.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <ButtonLink to="/contact" data-testid="cta-enquiry-button">
                  Make an enquiry
                </ButtonLink>
                <ButtonLink
                  to="/easy-read"
                  variant="secondary"
                  className="!bg-transparent !text-white !border-stone-600 hover:!bg-stone-800"
                  data-testid="cta-easy-read-button"
                >
                  Read the Easy Read page
                </ButtonLink>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4 rounded-2xl bg-stone-800 p-5">
                <Phone className="h-6 w-6 text-orange-400 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm text-stone-400">Call us</p>
                  <p className="text-lg font-medium text-white">{CONTACT.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl bg-stone-800 p-5">
                <Mail className="h-6 w-6 text-orange-400 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm text-stone-400">Email us</p>
                  <p className="text-lg font-medium text-white">{CONTACT.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {lightbox !== null && (
        <Lightbox
          images={MOSAIC}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
        />
      )}
    </>
  );
}
